import dayjs from 'dayjs';
import head from 'lodash/head';
import uniq from 'lodash/uniq';
import { createSelector } from 'reselect';

import { RootState } from 'store/types';

import { TimeSlot } from './types';
import {
  appointmentDateSelector,
  appointmentLocationSelector
} from '../appointmentDetails/selectors';

export const timeSlotsSelector = (state: RootState) => state.booking.appointment.timeSlots;

export const timeSlotsFetchingSelector = createSelector(
  timeSlotsSelector,
  timeSlots => timeSlots.isFetching
);

export const timeSlotsErrorSelector = createSelector(
  timeSlotsSelector,
  timeSlots => timeSlots.error
);

export const timeSlotsDataSelector = createSelector(timeSlotsSelector, timeSlots => timeSlots.data);

export const datesWithTimeSlotsSelector = createSelector([timeSlotsDataSelector], timeSlots => {
  if (!timeSlots || !timeSlots.length) return [];

  return uniq(timeSlots?.map(ts => ts.datetime).map(dt => head(dt.split('T')))).reduce(
    (hash, day) => ({ ...hash, [day]: true }),
    {}
  );
});

export const timeSlotsByAppointmentDateSelector = createSelector(
  [appointmentDateSelector, appointmentLocationSelector, timeSlotsDataSelector],
  (appointmentDate, appointmentLocation, timeSlots) => {
    if (!appointmentDate || !appointmentLocation || !timeSlots) {
      return [];
    }

    const res = new Map<string, TimeSlot[]>();

    const { locationId } = appointmentLocation;
    for (const timeSlot of timeSlots) {
      const isSameDay = dayjs(timeSlot.datetime).isSame(dayjs(appointmentDate), 'day');

      if (isSameDay && locationId) {
        const curr = res.get(locationId);

        if (curr) {
          res.set(locationId, [...curr, timeSlot]);
        } else {
          res.set(locationId, [timeSlot]);
        }
      }
    }
    return [...res.entries()].map(([location, slots]) => ({
      location,
      timeSlots: slots
    }));
  }
);
